import React from 'react'

import { AdminContext } from 'contexts/AdminContext'

import { Formik } from 'formik'

import { Button, Table } from '_zari/core'

import {
  TiposDeDocumentosRelacionadosCuentaContableConstant,
  ModalidadCalculosDeDepreciacionConstant
} from 'constants/index'

import { KeyEventUtil } from 'utils'

import * as FormGroups from './formGroups'
import * as Modals from './modals'

const RowTemplate = ({ row, rowIndex, updateStatus, handleEliminar, handleAgregarFila, esUltimaFila }) => {
  const __adminContext = React.useContext(AdminContext)

  const [mostrarCajaBancosModal, setMostrarCajaBancosModal] = React.useState(false)
  const [mostrarDocumentoVariosModal, setMostrarDocumentoVariosModal] = React.useState(false)
  const [mostrarActivoDepreciacionModal, setMostrarActivoDepreciacionModal] = React.useState(false)
  const [mostrarPorDefectoModal, setMostrarPorDefectoModal] = React.useState(false)

  const mainFormikRef = React.useRef(null)
  const modalDataRef = React.useRef({})
  const modalAvanzadoRef = React.useRef(false)

  React.useEffect(() => {
    if (!row.cuentaContable) {
      KeyEventUtil.leaveOnKeyEvent(`select-det-cuentaContable-${rowIndex}`)
    }
  }, [row.cuentaContable, rowIndex])

  const formInitialValues = React.useMemo(() => {
    return {
      _idGlosa: row._idGlosa || '',
      _idCentroCostoNivel1: row._idCentroCostoNivel1 || '',
      _idCentroCostoNivel2: row._idCentroCostoNivel2 || '',
      centroCostoNivel2Info: row.centroCostoNivel2Info || '',
      cuentaContable: row.cuentaContable || '',
      cuentaDestino: row.cuentaDestino || '',
      cuentaContableInfo: row.cuentaContableInfo || '',
      cuentaContableTipoDocumento: row.cuentaContableTipoDocumento || '',
      dh: row.dh || '',
      esCuentaActivoFijo: row.esCuentaActivoFijo || false,
      importeDebe: row.importeDebe || '',
      importeHaber: row.importeHaber || '',
      importeDebeSecundario: row.importeDebeSecundario || '',
      importeHaberSecundario: row.importeHaberSecundario || '',
      modalValido: row.modalValido || false,
      moneda: row.moneda || 'SOLES',
      glosa: row.glosa || '',
      flagCentroCosto: row.flagCentroCosto || false,
      tipoCambio: row.tipoCambio || '',
      tipoAfectacion: row.tipoAfectacion || ''
    }
  }, [row])

  const updateStore = React.useCallback(() => {
    const cambios = mainFormikRef.current.values
    const importeDebeSecundario = parseFloat(
      cambios.moneda === 'SOLES'
        ? parseFloat(cambios.importeDebe || '0') / parseFloat(cambios.tipoCambio || '1')
        : parseFloat(cambios.importeDebe || '0') * parseFloat(cambios.tipoCambio || '0')
    )
    const importeHaberSecundario = parseFloat(
      cambios.moneda === 'SOLES'
        ? parseFloat(cambios.importeHaber || '0') / parseFloat(cambios.tipoCambio || '1')
        : parseFloat(cambios.importeHaber || '0') * parseFloat(cambios.tipoCambio || '0')
    )
    updateStatus({
      ...row,
      ...mainFormikRef.current.values,
      importeDebeSecundario,
      importeHaberSecundario
    })
  }, [updateStatus, row])

  const clickMostrarModal = React.useCallback(values => {
    modalAvanzadoRef.current = false
    modalDataRef.current = values
    if (
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.BANCOS ||
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.CAJA
    ) {
      setMostrarCajaBancosModal(true)
    } else if (
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.DOCUMENTOS_VARIOS
    ) {
      setMostrarDocumentoVariosModal(true)
    } else if (
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.ACTIVO_FIJO ||
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.DEPRECIACION
    ) {
      setMostrarActivoDepreciacionModal(true)
    }
  }, [])

  const submitCajaBancoModal = React.useCallback(
    formData => {
      const nuevosValores = {
        ...modalDataRef.current,
        ...formData
      }
      mainFormikRef.current.setValues(nuevosValores)
      setMostrarCajaBancosModal(false)

      setTimeout(() => {
        updateStore()
      }, 200)
    },
    [updateStore]
  )

  const submitDocumentosVariosModal = React.useCallback(
    formData => {
      const nuevosValores = {
        ...modalDataRef.current,
        ...formData
      }
      mainFormikRef.current.setValues(nuevosValores)
      setMostrarDocumentoVariosModal(false)

      setTimeout(() => {
        updateStore()
      }, 200)
    },
    [updateStore]
  )

  const submitActivoDepreciacionModal = React.useCallback(
    formData => {
      const nuevosValores = {
        ...modalDataRef.current,
        ...formData
      }
      mainFormikRef.current.setValues(nuevosValores)
      setMostrarActivoDepreciacionModal(false)

      setTimeout(() => {
        updateStore()
      }, 200)
    },
    [updateStore]
  )

  const submitPorDefectoModal = React.useCallback(
    formData => {
      const nuevosValores = {
        ...modalDataRef.current,
        ...formData
      }
      mainFormikRef.current.setValues(nuevosValores)
      setMostrarPorDefectoModal(false)

      setTimeout(() => {
        updateStore()
      }, 200)
    },
    [updateStore]
  )

  const clickAvanzado = React.useCallback(values => {
    modalAvanzadoRef.current = true
    modalDataRef.current = values
    if (
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.BANCOS ||
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.CAJA
    ) {
      setMostrarCajaBancosModal(true)
    } else if (
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.DOCUMENTOS_VARIOS
    ) {
      setMostrarDocumentoVariosModal(true)
    } else if (
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.ACTIVO_FIJO ||
      values.cuentaContableTipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.DEPRECIACION
    ) {
      setMostrarActivoDepreciacionModal(true)
    } else {
      setMostrarPorDefectoModal(true)
    }
  }, [])

  const aplicaDestino = React.useCallback(
    cuentaContable => {
      if (__adminContext.modalidadCalculoDeDepreciacion !== ModalidadCalculosDeDepreciacionConstant.CLASE9) {
        return false
      }

      if (!cuentaContable) {
        return false
      }

      if (
        cuentaContable.startsWith('61') ||
        cuentaContable.startsWith('62') ||
        cuentaContable.startsWith('63') ||
        cuentaContable.startsWith('64') ||
        cuentaContable.startsWith('65') ||
        cuentaContable.startsWith('67') ||
        cuentaContable.startsWith('68') //Se añadió a solicitud de Junior para cuadre UIGV
      ) {
        return true
      } else {
        return false
      }
    },
    [__adminContext.modalidadCalculoDeDepreciacion]
  )

  return (
    <>
      <Formik initialValues={formInitialValues} innerRef={mainFormikRef}>
        {({ errors, touched, isSubmitting, values, handleBlur, handleChange, setFieldValue, setValues }) => (
          <Table.Row className='zari-slim-form'>
            <Table.Cell>
              <FormGroups.CuentaContableFormGroup
                iError={errors.cuentaContable}
                iTouched={touched.cuentaContable}
                iValue={values.cuentaContableInfo}
                setFieldValue={setFieldValue}
                updateStore={updateStore}
                rowIndex={rowIndex}
                setValues={setValues}
                values={values}
              />
            </Table.Cell>
            <Table.Cell align='center'>
              {values.flagCentroCosto ? (
                <FormGroups.CentroCostoNivel1FormGroup
                  iError={errors._idCentroCostoNivel1}
                  iTouched={touched._idCentroCostoNivel1}
                  fIsSubmitting={isSubmitting}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  updateStore={updateStore}
                  rowIndex={rowIndex}
                />
              ) : (
                '--'
              )}
            </Table.Cell>
            <Table.Cell align='center'>
              {values.flagCentroCosto && values._idCentroCostoNivel1 ? (
                <FormGroups.CentroCostoNivel2FormGroup
                  iError={errors._idCentroCostoNivel2}
                  iTouched={touched._idCentroCostoNivel2}
                  setFieldValue={setFieldValue}
                  iValue={values.centroCostoNivel2Info}
                  idNodoPadre={values._idCentroCostoNivel1}
                  handleBlur={handleBlur}
                  updateStore={updateStore}
                  rowIndex={rowIndex}
                />
              ) : (
                '--'
              )}
            </Table.Cell>
            <Table.Cell align='center'>
              {aplicaDestino(values.cuentaContable) ? (
                <FormGroups.CuentaDestinoFormGroup
                  iError={errors.cuentaDestino}
                  iTouched={touched.cuentaDestino}
                  fIsSubmitting={isSubmitting}
                  updateStore={updateStore}
                  rowIndex={rowIndex}
                  handleChange={handleChange}
                />
              ) : (
                '--'
              )}
            </Table.Cell>
            <Table.Cell>
              <FormGroups.MonedaFormGroup
                vDh={values.dh}
                iError={errors.moneda}
                iTouched={touched.moneda}
                fIsSubmitting={isSubmitting}
                handleChange={handleChange}
                updateStore={updateStore}
                rowIndex={rowIndex}
              />
            </Table.Cell>
            <Table.Cell>
              <FormGroups.ImporteDebeFormGroup
                vTipoCambio={values.tipoCambio}
                vMoneda={values.moneda}
                iError={errors.importeDebe}
                iTouched={touched.importeDebe}
                fIsSubmitting={isSubmitting}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                updateStore={updateStore}
                rowIndex={rowIndex}
              />
            </Table.Cell>
            <Table.Cell>
              <FormGroups.ImporteHaberFormGroup
                vTipoCambio={values.tipoCambio}
                vMoneda={values.moneda}
                iError={errors.importeHaber}
                iTouched={touched.importeHaber}
                fIsSubmitting={isSubmitting}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                updateStore={updateStore}
                rowIndex={rowIndex}
                tipoDocumentoValido={TiposDeDocumentosRelacionadosCuentaContableConstant.__valuesOrigenDiario.some(
                  x => x === values.cuentaContableTipoDocumento
                )}
                modalValido={values.modalValido}
                mostrarModal={() => clickMostrarModal({ ...row, ...values })}
                handleAgregarFila={handleAgregarFila}
                esUltimaFila={esUltimaFila}
              />
            </Table.Cell>
            <Table.Cell align='center'>
              {TiposDeDocumentosRelacionadosCuentaContableConstant.__valuesOrigenDiario.some(
                x => x === values.cuentaContableTipoDocumento
              ) ? (
                <Button
                  id='nuevoasiento-mostrarModal-button'
                  block
                  onClick={() => clickMostrarModal({ ...row, ...values })}
                  color={!values.modalValido ? 'warning' : 'info'}
                  outline
                >
                  &#x1F896;
                </Button>
              ) : (
                '--'
              )}
            </Table.Cell>
            <Table.Cell>
              <Table.Dropdown>
                {({ handleClose }) => [
                  <Table.Dropdown.Item
                    key='editar'
                    handleClose={handleClose}
                    onClick={e => clickAvanzado({ ...row, ...values })}
                  >
                    Editar
                  </Table.Dropdown.Item>,
                  <Table.Dropdown.Item key='eliminar' handleClose={handleClose} onClick={e => handleEliminar(row)}>
                    Eliminar
                  </Table.Dropdown.Item>
                ]}
              </Table.Dropdown>
            </Table.Cell>
          </Table.Row>
        )}
      </Formik>
      {mostrarCajaBancosModal && (
        <Modals.CajaBancoModal
          show={mostrarCajaBancosModal}
          handleClose={() => setMostrarCajaBancosModal(false)}
          modalData={modalDataRef.current}
          submitFunction={submitCajaBancoModal}
          esAvanzado={modalAvanzadoRef.current}
        />
      )}
      {mostrarDocumentoVariosModal && (
        <Modals.DocumentosVariosModal
          show={mostrarDocumentoVariosModal}
          handleClose={() => setMostrarDocumentoVariosModal(false)}
          modalData={modalDataRef.current}
          submitFunction={submitDocumentosVariosModal}
          esAvanzado={modalAvanzadoRef.current}
        />
      )}
      {mostrarActivoDepreciacionModal && (
        <Modals.ActivoDepreciacionModal
          show={mostrarActivoDepreciacionModal}
          handleClose={() => setMostrarActivoDepreciacionModal(false)}
          modalData={modalDataRef.current}
          submitFunction={submitActivoDepreciacionModal}
          esAvanzado={modalAvanzadoRef.current}
        />
      )}
      {mostrarPorDefectoModal && (
        <Modals.PorDefectoModal
          show={mostrarPorDefectoModal}
          handleClose={() => setMostrarPorDefectoModal(false)}
          modalData={modalDataRef.current}
          submitFunction={submitPorDefectoModal}
        />
      )}
    </>
  )
}

export default React.memo(RowTemplate)
